/**
 * Reglas compartidas por el alta de tomas de pared (diálogo masivo y tabla
 * editable del listado).
 *
 * Lo importante aquí son dos cosas que se repiten y es fácil equivocar:
 *
 *   1. Qué es un puerto de patch panel LIBRE. Una toma lo ocupa por FK
 *      (`WallOutlet.panel_port_id`) y un latiguillo lo ocupa por
 *      `PortConnection`; además un hueco sin keystone no vale. Si esta regla
 *      se copia en dos sitios, uno de los dos se queda atrás.
 *   2. El código siguiente de una familia (`T-001` → `T-002`) conservando el
 *      ancho de los ceros a la izquierda.
 */

/** Texto visible de un puerto de panel: su etiqueta o `P<n>`. */
export function etiquetaPuerto(puerto) {
  return puerto?.label || (puerto?.port_number != null ? `P${puerto.port_number}` : '—');
}

/** Motivo por el que un puerto de patch panel ya está pillado (o null). */
export function motivoOcupadoPanel(puerto) {
  if (!puerto) return null;
  if (puerto.wall_outlet) return `toma ${puerto.wall_outlet.code}`;
  if (puerto.connection) {
    const otro = puerto.connection.other_end || {};
    return otro.element_name ? `latiguillo a ${otro.element_name}` : 'parcheado';
  }
  if (puerto.is_patched) return 'parcheado';
  return null;
}

/** ¿El puerto de panel se puede usar para una toma nueva? */
export function puertoPanelLibre(puerto) {
  return Boolean(puerto)
    && puerto.is_active !== false
    && puerto.is_populated !== false
    && !motivoOcupadoPanel(puerto);
}

/**
 * Código siguiente conservando prefijo y ceros: `T-001` → `T-002`,
 * `PALCO-09` → `PALCO-10`. Si el código no acaba en número no hay nada que
 * incrementar: se devuelve vacío para que lo escriba el usuario (mejor vacío
 * que un código repetido que el backend va a rechazar).
 */
export function siguienteCodigo(code) {
  const texto = String(code || '').trim();
  const partes = texto.match(/^(.*?)(\d+)$/);
  if (!partes) return '';
  const [, prefijo, digitos] = partes;
  const siguiente = String(Number(digitos) + 1).padStart(digitos.length, '0');
  return `${prefijo}${siguiente}`;
}

/**
 * Primer puerto libre con número MAYOR que `desdeNumero` (para continuar la
 * serie). Con `desdeNumero` nulo devuelve el primero libre. `usados` son los
 * ids ya elegidos por otras filas de la misma remesa.
 */
export function siguientePuertoLibre(puertos, desdeNumero = null, usados = new Set()) {
  const libres = (puertos || [])
    .filter((p) => puertoPanelLibre(p) && !usados.has(String(p.id)))
    .sort((a, b) => Number(a.port_number) - Number(b.port_number));
  if (desdeNumero == null) return libres[0] || null;
  return libres.find((p) => Number(p.port_number) > Number(desdeNumero)) || null;
}
